import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { newImageElement, newTextElement } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import TopUpModal from "../components/TopUpModal";
import { toast } from "sonner";
import {
  ArrowLeft, MagicWand, Image as ImageIcon, Stack, UsersThree, X, Crown,
  Sparkle, DownloadSimple, PencilLine, Lightning, FloppyDisk, BookmarkSimple, Trash,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const readPreview = (file) => URL.createObjectURL(file);

function UploadBox({ id, icon: Icon, label, hint, accent, multiple, max, files, onAdd, onRemove }) {
  const inputRef = useRef(null);
  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) onAdd(picked);
    e.target.value = "";
  };
  const onDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
    if (dropped.length) onAdd(dropped);
  };
  return (
    <div
      data-testid={`upload-box-${id}`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className="rounded-2xl border border-white/10 bg-[#111] p-4 flex flex-col"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}><Icon size={17} weight="fill" className="text-white" /></div>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight truncate">{label}</p>
          <p className="text-[11px] text-zinc-500 leading-tight">{hint}</p>
        </div>
        {multiple && <span className="ml-auto text-[10px] font-semibold text-zinc-400">{files.length}{max ? `/${max}` : ""}</span>}
      </div>

      {files.length > 0 && (
        <div className={`grid ${multiple ? "grid-cols-4" : "grid-cols-1"} gap-2 mb-3`}>
          {files.map((f, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden ring-1 ring-white/10 aspect-square bg-black/40">
              <img src={readPreview(f)} alt={f.name} className="w-full h-full object-cover" />
              <button data-testid={`remove-${id}-${i}`} onClick={() => onRemove(i)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={11} className="text-white" /></button>
            </div>
          ))}
        </div>
      )}

      {(multiple || files.length === 0) && (!max || files.length < max) && (
        <button
          data-testid={`pick-${id}`}
          onClick={() => inputRef.current?.click()}
          className="mt-auto w-full rounded-lg border border-dashed border-white/15 hover:border-blue-500/60 hover:bg-white/[0.03] py-3 text-xs font-medium text-zinc-400 hover:text-white transition-colors"
        >
          {files.length === 0 ? "Click or drag to upload" : "+ Add more"}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple} onChange={onPick} className="hidden" data-testid={`input-${id}`} />
    </div>
  );
}

export default function StudioReplication() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPro, openUpgrade, sub: subInfo, refresh: refreshSub } = useSubscription();
  const canUse = isPro || !!user?.is_admin;

  const [reference, setReference] = useState([]);
  const [logos, setLogos] = useState([]);
  const [performers, setPerformers] = useState([]);
  const [eventInfo, setEventInfo] = useState("");
  const [customDirection, setCustomDirection] = useState("");
  const [mode, setMode] = useState("layered");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [opening, setOpening] = useState(false);
  const [presets, setPresets] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);

  const loadPresets = useCallback(async () => {
    try {
      const res = await api.get("/studio/presets");
      setPresets(res.data || []);
    } catch (e) { /* non-blocking */ }
  }, []);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  const applyPreset = (p) => {
    setEventInfo(p.event_info || "");
    setCustomDirection(p.custom_direction || "");
    toast.success(`Loaded preset “${p.name}”`);
  };

  const openSaveDialog = () => {
    if (!eventInfo.trim() && !customDirection.trim()) {
      toast.error("Write some Event Info or Creative Direction first.");
      return;
    }
    setPresetName("");
    setSaveOpen(true);
  };

  const savePreset = async () => {
    if (!presetName.trim()) { toast.error("Give your preset a name."); return; }
    setSavingPreset(true);
    try {
      await api.post("/studio/presets", {
        name: presetName.trim(), event_info: eventInfo, custom_direction: customDirection,
      });
      toast.success("Preset saved");
      setSaveOpen(false);
      await loadPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preset");
    } finally {
      setSavingPreset(false);
    }
  };

  const deletePreset = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/studio/presets/${id}`);
      setPresets((ps) => ps.filter((p) => p.id !== id));
    } catch (err) {
      toast.error("Could not delete preset");
    }
  };

  const addFiles = (setter, limit) => (picked) =>
    setter((prev) => (limit ? [...prev, ...picked].slice(0, limit) : [...prev, ...picked]));
  const removeAt = (setter) => (i) => setter((prev) => prev.filter((_, idx) => idx !== i));

  const execute = useCallback(async () => {
    if (!canUse) { openUpgrade("The AI Style Replication Studio is a Pro feature. Upgrade to compose studio-grade flyers."); return; }
    if (reference.length === 0) { toast.error("Add an Artistic Reference Flyer (Box A)."); return; }
    if (performers.length === 0) { toast.error("Add at least one Performer photo (Box C)."); return; }

    const fd = new FormData();
    fd.append("reference", reference[0]);
    performers.forEach((f) => fd.append("performers", f));
    logos.forEach((f) => fd.append("logos", f));
    fd.append("event_info", eventInfo);
    fd.append("custom_direction", customDirection);
    fd.append("mode", mode);

    setRunning(true);
    setResult(null);
    try {
      const res = await api.post("/studio/replicate-style", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setResult(res.data);
      toast.success("Your studio composite is ready!");
      refreshSub?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) openUpgrade(detail || "Upgrade to Pro to use the Studio.");
      else toast.error(detail || "Replication failed. Please try again.");
    } finally {
      setRunning(false);
    }
  }, [canUse, reference, performers, logos, eventInfo, customDirection, mode, openUpgrade]);

  const downloadResult = () => {
    if (!result) return;
    const a = document.createElement("a");
    a.href = result.image_base64;
    a.download = "flyerstudio-ai-replication.png";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const openInEditor = async () => {
    if (!result) return;
    setOpening(true);
    try {
      const layout = result.layout;
      let elements;
      if (layout?.background?.storage_path) {
        // Layered handoff: clean background image + each text block as an editable text layer.
        const bg = newImageElement(fileUrl(layout.background.storage_path), {
          x: 0, y: 0, width: result.width, height: result.height, zIndex: 1,
          locked: true, name: "Background",
        });
        const textEls = (layout.text_layers || []).map((t, i) =>
          newTextElement({
            text: t.content,
            x: t.x, y: t.y, width: t.width, height: t.height,
            fontSize: t.font_size, color: t.color,
            textAlign: t.text_align, align: t.text_align,
            fontWeight: String(t.font_weight || "700"),
            zIndex: i + 2,
            name: (t.content || "Text").slice(0, 24),
          })
        );
        elements = [bg, ...textEls];
      } else {
        // Fallback: single flat image.
        elements = [newImageElement(fileUrl(result.storage_path), {
          x: 0, y: 0, width: result.width, height: result.height, zIndex: 1,
        })];
      }
      const res = await api.post("/designs", {
        title: "AI Style Replication",
        width: result.width, height: result.height,
        background: "#000000", elements, category: "flyer",
      });
      navigate(`/editor/${res.data.id}`);
    } catch (e) {
      toast.error("Could not open the design in the editor.");
      setOpening(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070707] text-white font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} data-testid="studio-back-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"><ArrowLeft size={18} /></button>
            <div className="flex items-center gap-2">
              <MagicWand size={20} weight="fill" className="text-blue-500" />
              <span className="font-display font-extrabold text-lg tracking-tight">AI Style Replication Studio</span>
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 text-[10px] font-bold ring-1 ring-amber-400/30"><Crown size={11} weight="fill" /> PRO</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="studio-credit-count" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/10 bg-white/5 text-sm">
              <Lightning weight="fill" size={15} style={{ color: "#FFD60A" }} /> {subInfo?.ai_remaining ?? "—"} <span className="text-zinc-500">credits</span>
            </span>
            <button onClick={() => setTopupOpen(true)} data-testid="studio-topup-btn" title="Need more credits? Top-up now" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-black text-sm font-bold transition-transform hover:scale-105" style={{ backgroundColor: "#FFD60A" }}>
              <Lightning weight="fill" size={15} /> <span className="hidden md:inline">Top-Up</span>
            </button>
          </div>
        </div>
      </header>

      <TopUpModal open={topupOpen} onClose={() => setTopupOpen(false)} onSuccess={refreshSub} />
      <main className="max-w-[1500px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
        {/* LEFT: asset zones + result */}
        <div className="space-y-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mb-1">Compose like a studio 🎛️</h1>
            <p className="text-zinc-400 max-w-2xl text-sm">Upload a reference style, your logos and performer photos. Our engine reads the reference with Gemini vision, composites a brand-new flyer with Gemini Nano Banana, then hands it to the editor as a clean background plus fully editable text layers.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <UploadBox id="reference" icon={ImageIcon} label="Box A · Reference Flyer" hint="1 artistic style image" accent="bg-blue-600" multiple={false} max={1} files={reference} onAdd={addFiles(setReference, 1)} onRemove={removeAt(setReference)} />
            <UploadBox id="logos" icon={Stack} label="Box B · Brand & Logos" hint="Transparent PNGs" accent="bg-fuchsia-600" multiple max={10} files={logos} onAdd={addFiles(setLogos, 10)} onRemove={removeAt(setLogos)} />
            <UploadBox id="performers" icon={UsersThree} label="Box C · Performers" hint="Up to 15 subject photos" accent="bg-emerald-600" multiple max={15} files={performers} onAdd={addFiles(setPerformers, 15)} onRemove={removeAt(setPerformers)} />
          </div>

          {/* Result / preview */}
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-5 min-h-[340px] flex items-center justify-center">
            {running ? (
              <div className="text-center" data-testid="studio-loading">
                <div className="relative mx-auto h-16 w-16 mb-4">
                  <Sparkle size={64} weight="fill" className="text-blue-500 animate-pulse" />
                </div>
                <p className="font-display text-lg font-bold">Replicating your style…</p>
                <p className="text-sm text-zinc-500 mt-1">Vision analysis → multi‑subject composition. This can take up to a minute.</p>
              </div>
            ) : result ? (
              <div className="w-full flex flex-col items-center" data-testid="studio-result">
                <img src={result.image_base64} alt="AI composite flyer" className="max-h-[460px] rounded-xl ring-1 ring-white/15 shadow-2xl object-contain" />
                {result.layout?.text_layers?.length > 0 && (
                  <p data-testid="studio-layers-note" className="text-[11px] text-emerald-300/90 mt-3 flex items-center gap-1.5">
                    <PencilLine size={12} weight="fill" /> Opens as {result.layout.text_layers.length} editable text layer{result.layout.text_layers.length > 1 ? "s" : ""} over a clean background
                  </p>
                )}
                <div className="flex items-center gap-3 mt-5 flex-wrap justify-center">
                  <button data-testid="studio-open-editor" onClick={openInEditor} disabled={opening} className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold transition-colors disabled:opacity-60">
                    <PencilLine size={18} weight="bold" /> {opening ? "Opening…" : "Open in FlyerStudio Editor"}
                  </button>
                  <button data-testid="studio-download" onClick={downloadResult} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors">
                    <DownloadSimple size={18} weight="bold" /> Download
                  </button>
                  <button data-testid="studio-regenerate" onClick={execute} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/5 hover:bg-white/10 text-zinc-300 font-semibold transition-colors">
                    <MagicWand size={18} weight="bold" /> Re‑run
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center text-zinc-600">
                <MagicWand size={42} weight="duotone" className="mx-auto mb-3 text-zinc-700" />
                <p className="text-sm">Your generated flyer will appear here.</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: creative control sidebar */}
        <aside className="lg:sticky lg:top-24 h-fit rounded-2xl border border-white/10 bg-[#111] p-5 flex flex-col gap-4">
          <p className="font-display text-lg font-bold flex items-center gap-2"><Lightning size={18} weight="fill" className="text-amber-300" /> Creative Control</p>

          {/* Prompt Preset library */}
          <div data-testid="preset-library" className="rounded-xl border border-white/10 bg-black/30 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.16em] text-zinc-400 font-bold flex items-center gap-1.5"><BookmarkSimple size={14} weight="fill" className="text-blue-400" /> Prompt Presets</span>
              <button data-testid="save-preset-btn" onClick={openSaveDialog} className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                <FloppyDisk size={13} weight="fill" /> Save current
              </button>
            </div>
            {presets.length === 0 ? (
              <p className="text-[11px] text-zinc-500">Save your winning Event Info + Creative Direction, then reuse them in one click.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                {presets.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`preset-chip-${p.id}`}
                    onClick={() => applyPreset(p)}
                    title={`Load “${p.name}”`}
                    className="group inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-white/5 hover:bg-blue-600 ring-1 ring-white/10 text-xs font-medium text-zinc-200 hover:text-white transition-colors max-w-full"
                  >
                    <span className="truncate max-w-[120px]">{p.name}</span>
                    <span data-testid={`delete-preset-${p.id}`} onClick={(e) => deletePreset(e, p.id)} className="h-4 w-4 inline-flex items-center justify-center rounded-full hover:bg-red-500/80 text-zinc-400 hover:text-white"><X size={10} weight="bold" /></span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-[0.16em] text-zinc-400 font-bold mb-2 block">1. Event Information &amp; Copy</label>
            <textarea
              data-testid="field-event-info"
              value={eventInfo}
              onChange={(e) => setEventInfo(e.target.value)}
              placeholder="Paste all raw flyer text here — event name, date, time, venue, lineup, ticket info, hashtags, fine print…"
              rows={9}
              className="w-full rounded-xl bg-black/40 border border-white/10 px-3.5 py-3 text-sm leading-relaxed outline-none focus:border-blue-500/60 resize-y min-h-[150px]"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-[0.16em] text-zinc-400 font-bold mb-2 block">2. Creative Direction &amp; Custom Prompts</label>
            <textarea
              data-testid="field-custom-direction"
              value={customDirection}
              onChange={(e) => setCustomDirection(e.target.value)}
              placeholder="Paste your master design prompt, layout rules, and formatting styles here — color direction, title typography, subject arrangement, mood, do's & don'ts…"
              rows={11}
              className="w-full rounded-xl bg-black/40 border border-white/10 px-3.5 py-3 text-sm leading-relaxed outline-none focus:border-blue-500/60 resize-y min-h-[180px]"
            />
          </div>

          <div data-testid="studio-mode-toggle" className="rounded-xl border border-white/10 bg-black/30 p-3">
            <p className="text-xs uppercase tracking-[0.16em] text-zinc-400 font-bold mb-2">Output mode</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="mode-flat-btn"
                onClick={() => setMode("flat")}
                className={`rounded-lg px-3 py-2.5 text-left transition-all ring-1 ${mode === "flat" ? "bg-blue-600/20 ring-blue-500 text-white" : "bg-white/5 ring-white/10 text-zinc-300 hover:bg-white/10"}`}
              >
                <span className="flex items-center gap-1.5 text-sm font-bold leading-tight"><ImageIcon size={15} weight="fill" /> Flat</span>
                <span className="block text-[10px] text-zinc-400 mt-0.5">Single image · 1 credit</span>
              </button>
              <button
                data-testid="mode-layered-btn"
                onClick={() => setMode("layered")}
                className={`rounded-lg px-3 py-2.5 text-left transition-all ring-1 ${mode === "layered" ? "bg-fuchsia-600/20 ring-fuchsia-500 text-white" : "bg-white/5 ring-white/10 text-zinc-300 hover:bg-white/10"}`}
              >
                <span className="flex items-center gap-1.5 text-sm font-bold leading-tight"><Stack size={15} weight="fill" /> Advanced Layered</span>
                <span className="block text-[10px] text-zinc-400 mt-0.5">Editable text layers · 2 credits</span>
              </button>
            </div>
            <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
              {mode === "layered"
                ? "Opens in the editor as a clean background + editable text layers (4 AI calls)."
                : "Fastest: returns a single flat flyer image (2 AI calls)."}
            </p>
          </div>

          <button
            data-testid="execute-replication-btn"
            onClick={execute}
            disabled={running}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-fuchsia-600 hover:from-blue-500 hover:to-fuchsia-500 text-white font-bold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-60 shadow-[0_8px_30px_rgba(59,130,246,0.35)]"
          >
            <MagicWand size={18} weight="fill" /> {running ? "Working…" : "Execute Professional Replication"}
          </button>
          {!canUse && (
            <p className="text-[11px] text-amber-300/80 text-center flex items-center justify-center gap-1"><Crown size={12} weight="fill" /> Pro feature — you'll be prompted to upgrade.</p>
          )}
        </aside>
      </main>

      {/* Save preset dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-sm" data-testid="save-preset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Save prompt preset</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">Stores the current Event Info &amp; Creative Direction so you can reuse them in one click.</DialogDescription>
          </DialogHeader>
          <input
            data-testid="preset-name-input"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") savePreset(); }}
            placeholder="e.g. Neon Nightclub · Gold Title"
            autoFocus
            className="w-full h-10 rounded-lg bg-black/40 border border-white/10 px-3 text-sm outline-none focus:border-blue-500/60"
          />
          <DialogFooter>
            <button onClick={() => setSaveOpen(false)} className="h-10 px-4 rounded-lg text-sm font-semibold text-zinc-300 hover:bg-white/5 transition-colors">Cancel</button>
            <button data-testid="confirm-save-preset-btn" onClick={savePreset} disabled={savingPreset} className="h-10 px-5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors disabled:opacity-60">{savingPreset ? "Saving…" : "Save preset"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
